import React from 'react';
import ShowMoreText from 'react-show-more-text';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';
import { FaCheck, FaTimes, FaInfoCircle } from 'react-icons/fa';
import { format, parseISO } from 'date-fns';

import FormattedPlural from '../../../components/FormattedPlural';
import FormattedCurrency from '../../../components/FormattedCurrency';
import classNames from 'classnames';
import { titleCase, publisherLabel } from '../../../utils';
import { GET_SPOTIFY_URI } from '../../../queries';
import CopyText from '../../../components/CopyText';
import SpotifyOpenLink from '../../../components/SpotifyOpenLink';

const GET_FREEZE_CONTENT_DETAIL = gql`
    query freezeContentObject($contentId: String!) {
        freezeContentObject(contentId: $contentId) {
            artStatus
            audioStatus
            audio_updated_date
            contentId
            copyright
            drmFree
            embargoed
            image_updated_date
            launchDate
            metadata_updated_date
            titleAcquisitionStatus
            supplement_updated_date
        }
    }
`;

export const convertToDuration = (secondsAmount) => {
    let hours = Math.floor(secondsAmount / 3600); //Get whole hours
    if (hours < 10) {
        hours = `0${hours}`;
    }
    secondsAmount -= hours * 3600;
    let minutes = Math.floor(secondsAmount / 60); //Get remaining minutes
    secondsAmount -= minutes * 60;
    return `${hours}:${minutes < 10 ? '0' + minutes : minutes}:${
        secondsAmount.toFixed() < 10 ? '0' + secondsAmount.toFixed() : secondsAmount.toFixed()
    }`;
};

export const contributorsList = (contributors) => {
    const newList = contributors.map((x) => x.fullName);
    return newList.join(', ');
};

export const getImageUrl = (sku, width = 360, square = true) => {
    const newUrl = new URL(`https://images.inaudio.com/v1/image/cover/${sku}`);
    if (square) {
        newUrl.searchParams.set('aspect', '1:1');
    }
    if (width) {
        newUrl.searchParams.set('width', width);
    }
    newUrl.searchParams.set('ts', Date.now());
    return newUrl.toString();
};

const Metadata = ({ book }) => {
    const { data: uriData } = useQuery(GET_SPOTIFY_URI, {
        variables: { contentId: book.contentId },
        skip: !book?.contentId
    });

    const { data: freezeObject } = useQuery(GET_FREEZE_CONTENT_DETAIL, {
        skip: !book?.contentId,
        variables: {
            contentId: book.contentId
        }
    });

    const { supplements = [], chapters = [] } = book.audioEngineBook.audiobook;

    return (
        <>
            {book && (
                <>
                    <section className="d-flex flex-wrap my-6">
                        <div className="basis-64">
                            <img className="d-block h-64 w-64 rounded" alt="Cover" src={getImageUrl(book.sku)} />
                            <small className="d-block mb-2 txt-center">
                                <a
                                    href={getImageUrl(book.sku, null, false)}
                                    title="See full size untransformed image"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Full size original
                                </a>
                                &nbsp;|&nbsp;
                                <a
                                    href={getImageUrl(book.sku, null, true)}
                                    title="See full size square image"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Full size square
                                </a>
                            </small>
                            {!!supplements.length && (
                                <ul className="list-flat mt-3">
                                    {supplements.map((supplement, index) => (
                                        <li key={`supplement_${index}`}>
                                            <a href={supplement.url} target="_blank" rel="noopener noreferrer">
                                                Access Supplemental Material ({index + 1}/{supplements.length})
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className="col-12 col-lg ml-xl-12">
                            <div className="mb-3">
                                <h2 className="mb-0">{book.title}</h2>
                                {book.subtitle && <h2 className="h4 txt-gray">{book.subtitle}</h2>}
                            </div>
                            <p>
                                {book.active && (
                                    <span className="badge badge-success mr-4">
                                        <FaCheck /> Active
                                    </span>
                                )}
                                {!book.active && (
                                    <span className="badge badge-warning mr-4">
                                        <FaTimes /> Inactive
                                    </span>
                                )}
                                {book.audioEngineBook.audiobook.chapterized && (
                                    <span className="badge badge-success">
                                        <FaCheck /> Chaptered
                                    </span>
                                )}
                                {!book.audioEngineBook.audiobook.chapterized && (
                                    <span className="badge badge-danger">
                                        <FaTimes /> Not-Chaptered
                                    </span>
                                )}
                            </p>
                            <div className="row">
                                <div className="col-12 col-md-6">
                                    <ul className="list-flat">
                                        <li className="my-2">
                                            <strong>
                                                <FormattedPlural
                                                    value={book.contributors.authors.length}
                                                    one="Author"
                                                    many="Authors"
                                                />
                                                :
                                            </strong>{' '}
                                            {book.contributors.authors.map((author, idx) => (
                                                <span className="pr-2" key={`${author.fullName}-${idx}`}>
                                                    <a href={`/search?q=${encodeURIComponent(author.fullName)}`}>
                                                        {author.fullName}
                                                    </a>
                                                </span>
                                            ))}
                                        </li>
                                        <li className="my-2">
                                            <strong>
                                                <FormattedPlural
                                                    value={book.contributors.narrators.length}
                                                    one="Narrator"
                                                    many="Narrators"
                                                />
                                                :
                                            </strong>{' '}
                                            {contributorsList(book.contributors.narrators)}
                                        </li>
                                        <li className="my-2">
                                            <strong>Publisher:</strong>{' '}
                                            {book.publisherName && (
                                                // href stays on canonical publisherName -- Cube's full-text
                                                // search is indexed on it, so linking the display name would
                                                // silently return nothing. Only the link TEXT changes.
                                                <a href={`/search?q=${encodeURIComponent(book.publisherName)}`}>
                                                    {publisherLabel(book.publisherName, book.publisherDisplayName)}
                                                </a>
                                            )}
                                            {!book.publisherName && 'N/A'}
                                        </li>
                                        <li className="my-2">
                                            <strong>Content ID:</strong> {book.contentId}
                                            <CopyText text={book.contentId} title="Copy Content ID" />
                                        </li>
                                        {!!uriData?.findagatewayMapping && (
                                            <li className="my-2">
                                                <strong>Spotify URI:</strong>{' '}
                                                <SpotifyOpenLink uri={uriData.findagatewayMapping} />
                                                <CopyText text={uriData.findagatewayMapping} title="Copy Book URI" />
                                            </li>
                                        )}
                                        {book.aggregator && (
                                            <li className="my-2">
                                                <strong>Aggregator:</strong>{' '}
                                                {book?.aggregator === 'Findaway Voices'
                                                    ? `${book?.aggregator} (${book?.publisherId})`
                                                    : book?.aggregator}
                                            </li>
                                        )}
                                    </ul>
                                </div>
                                <div className="col-12 col-md-6">
                                    <ul className="list-flat">
                                        <li className="my-2">
                                            <strong>Series Name: </strong>
                                            {book?.seriesTitle ? book?.seriesTitle : 'None'}
                                        </li>
                                        <li className="my-2">
                                            <strong>Series Number: </strong>
                                            {book?.seriesNumber ? book?.seriesNumber : 'None'}
                                        </li>
                                        <li className="my-2">
                                            <strong>SKU: </strong>
                                            {book?.sku}
                                        </li>
                                        <li className="my-2">
                                            <strong>NetSuite ID: </strong>
                                            {book?.netsuiteId}
                                        </li>
                                        {!!book?.publisherId && (
                                            <li className="my-2">
                                                <strong>Publisher ID:</strong> {book.publisherId}
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>
                    <hr className="mb-0 d-xs-none d-sm-block" />
                    <section className="row">
                        <div className="mb-8 col-12 col-md-7 col-xl-8">
                            <h4 className="mt-6 col-12">Description</h4>
                            <div className="col-12 mb-8">
                                <ShowMoreText lines={2}>
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: book.description
                                        }}
                                    />
                                </ShowMoreText>
                            </div>

                            <div>
                                <h4>Last Updates</h4>
                                <table className="table table-sm mb-6">
                                    <thead>
                                        <tr className="bg-light">
                                            <th>Component</th>
                                            <th>Last Updated</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <th>
                                                Metadata{' '}
                                                <FaInfoCircle
                                                    className="mx-1 fa-info-circle"
                                                    color="#3a4958"
                                                    data-rh="The last time the Metadata changed. This does not include Pricing, and does include changes to the chapter array/playlist"
                                                    size={16}
                                                />
                                            </th>
                                            <td>
                                                <span>
                                                    {!!freezeObject?.freezeContentObject?.metadata_updated_date &&
                                                        format(
                                                            parseISO(
                                                                freezeObject?.freezeContentObject?.metadata_updated_date
                                                            ),
                                                            'yyyy-MM-dd'
                                                        )}
                                                </span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Image</th>
                                            <td>
                                                {!!freezeObject?.freezeContentObject?.image_updated_date &&
                                                    format(
                                                        parseISO(freezeObject?.freezeContentObject?.image_updated_date),
                                                        'yyyy-MM-dd'
                                                    )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>
                                                Audio{' '}
                                                <FaInfoCircle
                                                    className="mx-1 fa-info-circle"
                                                    color="#3a4958"
                                                    data-rh="The last time the audio was updated. This does not include delivery to push partners or encoding to HLS."
                                                    size={16}
                                                />
                                            </th>
                                            <td>
                                                <span>
                                                    {!!chapters.length &&
                                                        !!freezeObject?.freezeContentObject?.audio_updated_date &&
                                                        format(
                                                            parseISO(
                                                                freezeObject?.freezeContentObject?.audio_updated_date
                                                            ),
                                                            'yyyy-MM-dd'
                                                        )}
                                                </span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Supplemental Material</th>
                                            <td>
                                                {!!supplements.length &&
                                                    !!freezeObject?.freezeContentObject?.supplement_updated_date &&
                                                    format(
                                                        parseISO(
                                                            freezeObject?.freezeContentObject?.supplement_updated_date
                                                        ),
                                                        'yyyy-MM-dd'
                                                    )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <h4 className="mt-6 col-12">List Prices</h4>
                            <div className="col-12 mb-8">
                                <h5 className="my-3">Non-localized Prices</h5>

                                <table className="table table-sm mb-6">
                                    <thead>
                                        <tr className="bg-light">
                                            <th>ISBN</th>
                                            <th>Channel</th>
                                            <th>Currency / Price</th>
                                            <th>Territories Included</th>
                                            <th>Territories Excluded</th>
                                            <th>Territories Exclusive</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {['library', 'retail'].map((channel) => {
                                            const metadata = book.modelRights?.[channel]?.[0];

                                            if (!metadata) {
                                                return null;
                                            }

                                            return (
                                                <tr key={`${channel}_${metadata.isbn}`}>
                                                    <td data-title="ISBN">{metadata.isbn || '--'}</td>
                                                    <td data-title="Channel">{titleCase(channel)}</td>
                                                    <td data-title="Currency/Price">
                                                        {metadata.listPrice === null ? (
                                                            'No Pricing Available'
                                                        ) : (
                                                            <>
                                                                <FormattedCurrency
                                                                    value={metadata.listPrice}
                                                                    currency={metadata.listPriceCurrency}
                                                                />{' '}
                                                                {metadata.listPriceCurrency}
                                                            </>
                                                        )}
                                                    </td>
                                                    {[
                                                        { key: 'territoriesIncluded', label: 'Territories Included' },
                                                        { key: 'territoriesExcluded', label: 'Territories Excluded' },
                                                        { key: 'territoriesExclusive', label: 'Territories Exclusive' }
                                                    ].map((territorialRights) => {
                                                        const territories = metadata[territorialRights.key];
                                                        return (
                                                            <td
                                                                key={territorialRights.key}
                                                                data-title={territorialRights.label}
                                                            >
                                                                {!!territories?.length && (
                                                                    <ShowMoreText lines={2}>
                                                                        {territories.join(', ')}
                                                                    </ShowMoreText>
                                                                )}
                                                                {!territories?.length && 'N/A'}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="mb-8 col-12 col-lg-4 col-lg-offset-1 col-xl-3">
                            <div className="bg-light px-5 pt-5 pb-1">
                                <h5>Other Metadata</h5>
                                <ul className="list-flat col-12 mb-3">
                                    <li className="my-3">
                                        <strong>
                                            AudioEngine Duration
                                            <FaInfoCircle
                                                className="mx-1 fa-info-circle"
                                                color="#3a4958"
                                                data-rh="This is the duration of the audio we have in the AudioEngine API.  Note that some slight differences may exist for push partners.  This should be very close in length to the AudioService Duration."
                                                size={16}
                                            />
                                            :{' '}
                                        </strong>

                                        {book?.audioEngineBook?.audiobook?.runtime &&
                                            book?.audioEngineBook?.audiobook?.runtime}
                                        {!book?.audioEngineBook?.audiobook?.runtime && 'N/A'}
                                    </li>
                                    <li className="my-3">
                                        <strong>
                                            AudioService Duration
                                            <FaInfoCircle
                                                className="mx-1 fa-info-circle"
                                                color="#3a4958"
                                                data-rh="This is the duration of the audio we received from the publisher.  This should be very close in length to the AudioEngine Duration."
                                                size={16}
                                            />
                                            :{' '}
                                        </strong>

                                        {book?.audioCohort &&
                                            convertToDuration(
                                                book.audioCohort?.fileList
                                                    .map((file) => file?.metadata?.duration ?? 0)
                                                    .reduce((a, b) => a + b, 0)
                                            )}
                                        {!book?.audioCohort && 'N/A'}
                                    </li>
                                    <li className="my-3">
                                        <strong>
                                            Publisher Duration
                                            <FaInfoCircle
                                                className="mx-1 fa-info-circle"
                                                color="#3a4958"
                                                data-rh="This is the duration of the book provided in the publisher metadata, and may not reflect the actual audio duration of the files received."
                                                size={16}
                                            />
                                            :
                                        </strong>

                                        {book?.publisherDuration && <> {book?.publisherDuration}</>}
                                        {!book?.publisherDuration && 'N/A'}
                                    </li>
                                    <li className="my-3">
                                        <strong>Language: </strong>
                                        {book?.language}
                                    </li>
                                    <li className="my-3">
                                        <strong>Release Date: </strong>
                                        {book?.originalReleaseDate}
                                    </li>

                                    <li className="my-3">
                                        <strong>Grade Level: </strong>
                                        {book?.audioEngineBook.audiobook?.grade_level}
                                    </li>
                                    <li className="my-3">
                                        <strong>Abridgement: </strong>
                                        {book?.abridgement}
                                    </li>
                                    <li className="my-3">
                                        <strong>Fiction Type: </strong>
                                        {book?.fictionType}
                                    </li>
                                    <li>
                                        <strong>Awards:</strong>
                                        {!!book?.awards?.length && (
                                            <ul className="list-flat pl-4">
                                                {book.awards.map((award, i) => (
                                                    <li
                                                        className={classNames({
                                                            'mb-2': i !== book.awards.length - 1
                                                        })}
                                                        key={`${book?.contentId}_award_${i}`}
                                                    >
                                                        {award}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {!book?.awards?.length && ' N/A'}
                                    </li>
                                    <li className="my-3">
                                        <strong>Keywords: </strong>
                                        {book.keywords?.length > 0 && book.keywords.join(', ')}
                                        {!book?.keywords?.length && 'N/A'}
                                    </li>
                                    {book?.bisacCodes && (
                                        <li>
                                            <strong className="d-block mb-1">BISAC Codes:</strong>
                                            <ul className="list-flat">
                                                {book.bisacCodes.map((bisacCode) => (
                                                    <li className="mb-2" key={bisacCode}>
                                                        {bisacCode}
                                                    </li>
                                                ))}
                                            </ul>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </section>
                </>
            )}
        </>
    );
};

export default Metadata;
